import { ConfigProvider, Typography } from 'antd';
import React, { FC } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { useHistory } from 'react-router';
import styled from 'styled-components';

import { onScroll } from '~/components/Scrollbars';
import colors from '~/constants/colors';
import theme from '~/constants/theme';
import { useDirection } from '~/hooks/i18nHooks';
import { getLayoutHeight, useHeaderHeight } from '~/hooks/useHeaderHeight';
import { useIsMobile } from '~/hooks/useScreenSizes';
import { LogoWithTextImg } from '~/img';
import HeaderIdentityMenu from '~/layouts/HeaderIdentityMenu';
import paths from '~/pages/paths';
import direction from '~/utils/direction';
import { getDecodedJwt, isSaleRep, isCallCenter, isContractor } from '~/utils/getDecodedJwt';

interface IProps {
  children: React.ReactNode;
}

const SalesHeader: FC = () => {
  const history = useHistory();
  const dir = useDirection();
  const decodedToken = getDecodedJwt();
  const goHome = () => history.push(paths.sales._);
  const Logo = (props: any) => <LogoWithTextImg {...props} />;
  const name = decodedToken?.name;

  // Show different title based on role
  const getDefaultTeam = () => {
    if (isSaleRep()) {
      return 'Network International Sales Representative Team';
    } else if (isCallCenter()) {
      return 'Network International Call Center Team';
    } else if (isContractor()) {
      return 'Network International Contractor Team';
    } else {
      return 'Network International Sales Team'; // Default for sales-Manager
    }
  };

  const team = decodedToken?.team ?? getDefaultTeam();

  return (
    <HeaderContainer className="Header" dir={dir}>
      <div className="Header-Wrapper">
        <Logo className="Header-LogoImg" onClick={goHome} />
        <div className="Header-Content">
          <div className="Header-salesInfo">
            <Typography.Text className="Header-Company">{team}</Typography.Text>
            <Typography.Text>{name}</Typography.Text>
          </div>
        </div>
        <HeaderIdentityMenu />
      </div>
    </HeaderContainer>
  );
};

const SalesDashboardLayout: FC<IProps> = (props) => {
  const { children } = props;
  const headerHeight = useHeaderHeight({ wizardHeader: false });
  const isMobile = useIsMobile();
  const scrollbarHeight = isMobile ? 64 : headerHeight;
  const dir = useDirection();
  return (
    <ConfigProvider componentSize="large" direction={dir}>
      <SalesHeader />
      <Container className="SalesDashboardLayout" withWrapper>
        <Scrollbars onUpdate={onScroll} style={getLayoutHeight(scrollbarHeight)}>
          <div className="SalesDashboardLayout-Wrapper">
            <div className="SalesDashboardLayout-Container">{children}</div>
          </div>
        </Scrollbars>
      </Container>
    </ConfigProvider>
  );
};

const Container = styled.div<{ withWrapper: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  min-height: ${({ withWrapper }) => (withWrapper ? `100vh` : `100%`)};
  background-color: ${colors.componentBackground};

  .SalesDashboardLayout-Wrapper {
    display: flex;
    flex: 1;
    width: 100%;
    flex-direction: column;
    align-items: center;
    padding: ${theme.paddingBase}px;

    .SalesDashboardLayout-Container {
      display: flex;
      flex: 1;
      width: 100%;
      max-width: ${theme.breakpoints.xl}px;
    }
  }
`;

const HeaderContainer = styled.header`
  display: flex;
  flex-direction: row;
  position: sticky;
  justify-content: center;
  padding: ${theme.paddingBase}px;
  top: 0;
  z-index: 10;
  background-color: ${colors.componentBackground};
  box-shadow: 0 0 5px ${colors.grey[100]};
  transition: box-shadow 0.2s ease-out;
  width: 100%;

  .Header-Wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: ${theme.breakpoints.xl}px;

    .Header-Content {
      display: flex;
      flex-direction: row;
      justify-content: center;
      align-items: center;
      padding: 0 ${theme.paddingBase}px 0 ${theme.paddingBase}px;
      width: 100%;
      //border: solid 1px;

      .Header-salesInfo {
        display: flex;
        flex-direction: row;

        .Header-Company {
          text-align: ${({ dir }) => (dir === 'ltr' ? 'right' : 'left')};
          font-weight: 600;
          ${direction.padding.right('1vw')};
          ${direction.margin.right('1vw')};
          ${direction.border.right(`solid 1px ${colors.grey[200]}`)};
        }
      }
    }

    .Header-LogoImg {
      height: 35px;
    }

    .ant-menu {
      border: 0;
    }

    .ant-menu-item::after {
      border-bottom-width: 4px; //!
      bottom: -${theme.paddingBase}px;
    }

    .ant-menu-item:hover::after {
      border-bottom-width: 4px;
    }

    .ant-menu-item:hover {
      color: ${colors.textColor};
    }

    .ant-menu-item-selected {
      color: ${colors.textColor};
      font-weight: 600;
    }

    .Header-BurgerMenu {
      color: ${colors.primaryColor};
      font-size: 36px;
      cursor: pointer;
    }

    .Header-MiddleContent__mobile {
      margin-top: 16px;
    }
  }
`;

export default SalesDashboardLayout;
